// src/pages/motolivraison/livreur-detail.page.jsx
//
// Détail d'un livreur MotoLivraison avec ses informations + permis + photo moto + stats.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Tag, Descriptions, Button, message, Space, Progress,
  Breadcrumb, Avatar, Spin, Table, Empty, Typography,
} from 'antd';
import {
  UserOutlined, ArrowLeftOutlined, RightOutlined, CheckCircleOutlined,
  StopOutlined, PlayCircleOutlined, ShoppingCartOutlined, PhoneOutlined,
  MailOutlined, EnvironmentOutlined, CarOutlined, WalletOutlined,
  InfoCircleOutlined, DollarOutlined, CloseCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from 'recharts';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motolivraisonService } from '../../common/services';
import { formatPhoneCI } from '../../common/helper';
import { LOCALE_TABLE, PAGINATION_TABLE, BTN_ACTION, COULEURS_ACTION, cmpTexte } from './table.helpers';
import './css/motolivraison-table.css';
import dayjs from 'dayjs';

const { Text } = Typography;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

const STATUT_CMD = {
  en_attente_paiement: { c: 'orange', l: 'Attente paiement' },
  payee: { c: 'blue', l: 'Payée' },
  en_attente_livreur: { c: 'gold', l: 'Attente livreur' },
  assignee: { c: 'cyan', l: 'Assignée' },
  recuperee: { c: 'cyan', l: 'Récupérée' },
  en_livraison: { c: 'geekblue', l: 'En livraison' },
  livree: { c: 'green', l: 'Livrée' },
  confirmee: { c: 'green', l: 'Confirmée' },
  soldee: { c: 'success', l: 'Soldée' },
  annulee: { c: 'red', l: 'Annulée' },
};

const LivreurDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [livreur, setLivreur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commandes, setCommandes] = useState([]);
  const [loadingCommandes, setLoadingCommandes] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getLivreurById(id);
      setLivreur(data);
    } catch (e) {
      message.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Commandes (missions) du livreur
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingCommandes(true);
      try {
        const r = await motolivraisonService.listCommandes({ livreurId: id, limit: 100 });
        if (alive) setCommandes(r?.commandes || []);
      } catch (e) { /* silencieux */ } finally { if (alive) setLoadingCommandes(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  const handleAction = async (action) => {
    try {
      const labels = { validate: 'approuvé', suspend: 'suspendu', reactivate: 'réactivé' };
      if (action === 'validate') await motolivraisonService.validateLivreur(id);
      else if (action === 'suspend') await motolivraisonService.suspendLivreur(id);
      else if (action === 'reactivate') await motolivraisonService.reactivateLivreur(id);
      message.success(`Livreur ${labels[action]}`);
      fetch();
    } catch (e) {
      message.error(e?.message || 'Erreur');
    }
  };

  if (loading || !livreur) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  const profile = livreur.livreurMotoProfile || {};

  // Statistiques calculées sur les commandes du livreur : elles reflètent
  // l'activité réelle, là où profile.stats est un compteur agrégé.
  const terminees = commandes.filter(c => ['livree', 'confirmee', 'soldee'].includes(c.statut));
  const annulees = commandes.filter(c => c.statut === 'annulee');
  const enCours = commandes.filter(c => ['assignee', 'recuperee', 'en_livraison'].includes(c.statut));
  const caTransporte = terminees.reduce((t, c) => t + Number(c.montantTTC || 0), 0);
  const panierMoyen = terminees.length ? caTransporte / terminees.length : 0;
  const tauxReussite = commandes.length ? (terminees.length / commandes.length) * 100 : 0;
  const derniereLivraison = terminees
    .map(c => c.livreeAt || c.createdAt)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))[0];

  // Activité des 8 dernières semaines (livraisons terminées)
  const activite = (() => {
    const semaines = [];
    for (let i = 7; i >= 0; i--) {
      const debut = dayjs().subtract(i, 'week').startOf('week');
      const fin = debut.endOf('week');
      semaines.push({
        semaine: debut.format('DD/MM'),
        livraisons: terminees.filter(c => {
          const d = dayjs(c.livreeAt || c.createdAt);
          return d.isAfter(debut) && d.isBefore(fin);
        }).length,
      });
    }
    return semaines;
  })();

  const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red' })[s] || 'default';
  const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu' })[s] || s || '—';

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/motolivraison/livreurs">Livreurs</Link></Breadcrumb.Item>
          <Breadcrumb.Item>{livreur.firstName} {livreur.lastName}</Breadcrumb.Item>
        </Breadcrumb>

        {/* ─── Bannière : l'espace du livreur ──────────────────────────── */}
        <Card style={{ marginBottom: 16, overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>
          <div style={{ padding: '22px 24px', background: 'linear-gradient(135deg, #1e3a8a, #1677ff)', color: '#fff' }}>
            <Row gutter={[16, 16]} align="middle" justify="space-between" wrap>
              <Col xs={24} lg={15}>
                <Space size={18} align="start" wrap={false} style={{ width: '100%' }}>
                  <Avatar
                    size={84}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#1890ff', border: '3px solid rgba(255,255,255,.85)', flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ color: '#fff', margin: 0, fontSize: 24, lineHeight: 1.2 }}>
                      {`${livreur.firstName || ''} ${livreur.lastName || ''}`.trim()}
                    </h2>
                    <Space size={[6, 6]} wrap style={{ marginTop: 8 }}>
                      <Tag color={statusColor(profile.status)} style={{ margin: 0 }}>{statusLabel(profile.status)}</Tag>
                      {profile.status === 'approved' && (
                        <Tag color={profile.isAvailable ? 'green' : 'default'} style={{ margin: 0 }}>
                          {profile.isAvailable ? '🟢 En ligne' : '⚫ Hors ligne'}
                        </Tag>
                      )}
                      {enCours.length > 0 && (
                        <Tag color="processing" style={{ margin: 0 }}>{enCours.length} mission(s) en cours</Tag>
                      )}
                      {profile.plaqueImmatriculation && (
                        <Tag style={{ margin: 0 }}>🏍️ {profile.plaqueImmatriculation}</Tag>
                      )}
                    </Space>
                    <div style={{ marginTop: 10, fontSize: 13, opacity: 0.92 }}>
                      <PhoneOutlined style={{ marginRight: 6 }} />{formatPhoneCI(livreur.phone)}
                      {livreur.email && (
                        <span style={{ marginLeft: 14 }}><MailOutlined style={{ marginRight: 6 }} />{livreur.email}</span>
                      )}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, opacity: 0.88 }}>
                      <EnvironmentOutlined style={{ marginRight: 6 }} />
                      {profile.cityId?.name || 'Ville non définie'}
                      <span style={{ marginLeft: 14 }}>
                        <ClockCircleOutlined style={{ marginRight: 6 }} />
                        Dernière livraison : {derniereLivraison ? dayjs(derniereLivraison).format('DD/MM/YYYY') : '—'}
                      </span>
                    </div>
                  </div>
                </Space>
              </Col>

              <Col xs={24} lg={9} style={{ textAlign: 'right' }}>
                <Space wrap style={{ justifyContent: 'flex-end' }}>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Retour</Button>
                  {profile.status === 'pending' && (
                    <Button icon={<CheckCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.valider)}
                      onClick={() => handleAction('validate')}>Approuver</Button>
                  )}
                  {profile.status === 'approved' && (
                    <Button icon={<StopOutlined />} style={BTN_ACTION(COULEURS_ACTION.danger)}
                      onClick={() => handleAction('suspend')}>Suspendre</Button>
                  )}
                  {profile.status === 'suspended' && (
                    <Button icon={<PlayCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.valider)}
                      onClick={() => handleAction('reactivate')}>Réactiver</Button>
                  )}
                </Space>
              </Col>
            </Row>
          </div>

          {/* Chiffres clés du livreur */}
          <Row style={{ borderTop: '1px solid #f0f0f0' }}>
            {[
              { label: 'Missions reçues', valeur: fmt(commandes.length), couleur: '#334155', icone: <ShoppingCartOutlined /> },
              { label: 'Livraisons terminées', valeur: fmt(terminees.length), couleur: '#52c41a', icone: <CheckCircleOutlined /> },
              { label: 'Annulées', valeur: fmt(annulees.length), couleur: '#f5222d', icone: <CloseCircleOutlined /> },
              { label: 'Montant transporté', valeur: `${fmt(caTransporte)} F`, couleur: '#fa8c16', icone: <DollarOutlined /> },
            ].map((k, i) => (
              <Col xs={12} md={6} key={k.label} style={{ padding: '14px 18px', borderLeft: i ? '1px solid #f0f0f0' : 'none' }}>
                <Space size={10} align="center">
                  <span style={{ background: `${k.couleur}1a`, color: k.couleur, width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {k.icone}
                  </span>
                  <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{k.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: k.couleur }}>{k.valeur}</div>
                  </div>
                </Space>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          {/* ─── Colonne principale : performance et missions ──────────── */}
          <Col xs={24} xl={17}>
            <Card size="small" title="Activité des 8 dernières semaines" style={{ marginBottom: 16 }}>
              {commandes.length ? (
                <div style={{ width: '100%', height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart data={activite} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="semaine" tick={{ fontSize: 11 }} tickMargin={8} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <RTooltip
                        formatter={(v) => [`${v} livraison${v > 1 ? 's' : ''}`, 'Terminées']}
                        labelFormatter={(l) => `Semaine du ${l}`}
                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      />
                      <Bar dataKey="livraisons" fill="#1677ff" radius={[4, 4, 0, 0]} barSize={26} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune mission enregistrée" />
              )}
            </Card>
            <Card size="small" title={<Space><ShoppingCartOutlined />Missions ({commandes.length})</Space>}>
          <Table
            className="moto-table"
            rowKey="_id"
            size="middle"
            loading={loadingCommandes}
            dataSource={commandes}
            locale={{ ...LOCALE_TABLE, emptyText: <Empty description="Aucune commande livrée" /> }}
            pagination={PAGINATION_TABLE('commande')}
            scroll={{ x: 1050 }}
            columns={[
              {
                title: 'Réf', key: 'ref', width: 110,
                render: (_, r) => <Text code>#{String(r._id).slice(-6).toUpperCase()}</Text>,
              },
              {
                title: 'Commerce', key: 'com', width: 200, ellipsis: true,
                sorter: (a, b) => cmpTexte(a.commerceId?.nom, b.commerceId?.nom),
                render: (_, r) => r.commerceId?.nom || '—',
              },
              {
                title: 'Client', key: 'cli', width: 200, ellipsis: true,
                sorter: (a, b) => cmpTexte(
                  a.clientNom || `${a.clientId?.firstName || ''} ${a.clientId?.lastName || ''}`,
                  b.clientNom || `${b.clientId?.firstName || ''} ${b.clientId?.lastName || ''}`,
                ),
                render: (_, r) => r.clientNom
                  || `${r.clientId?.firstName || ''} ${r.clientId?.lastName || ''}`.trim()
                  || '—',
              },
              {
                title: 'Téléphone', key: 'tel', width: 150,
                render: (_, r) => (
                  <span style={{ whiteSpace: 'nowrap' }}>{formatPhoneCI(r.clientPhone || r.clientId?.phone)}</span>
                ),
              },
              {
                title: 'Montant', dataIndex: 'montantTTC', key: 'mt', align: 'right', width: 130,
                sorter: (a, b) => (a.montantTTC || 0) - (b.montantTTC || 0),
                render: (v) => <Text strong>{fmt(v)} F</Text>,
              },
              {
                title: 'Statut', dataIndex: 'statut', key: 'st', width: 140,
                filters: Object.entries(STATUT_CMD).map(([value, o]) => ({ text: o.l, value })),
                onFilter: (value, r) => r.statut === value,
                render: (v) => <Tag color={STATUT_CMD[v]?.c || 'default'}>{STATUT_CMD[v]?.l || v}</Tag>,
              },
              {
                title: 'Date', dataIndex: 'createdAt', key: 'dt', width: 160,
                defaultSortOrder: 'descend',
                sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
                render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—',
              },
            ]}
          />
        </Card>

          </Col>

          {/* ─── Colonne latérale : identité, moto et compte ───────────── */}
          <Col xs={24} xl={7}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card size="small" title={<Space><InfoCircleOutlined />Performance</Space>}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span style={{ color: '#8c8c8c' }}>Taux de réussite</span>
                    <strong>{tauxReussite.toFixed(0)} %</strong>
                  </div>
                  <Progress
                    percent={Math.round(tauxReussite)}
                    showInfo={false}
                    strokeColor={tauxReussite >= 80 ? '#52c41a' : tauxReussite >= 50 ? '#fa8c16' : '#f5222d'}
                  />
                </div>
                <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', width: 130 }}>
                  <Descriptions.Item label="Panier moyen">{fmt(Math.round(panierMoyen))} F</Descriptions.Item>
                  <Descriptions.Item label="Missions en cours">{fmt(enCours.length)}</Descriptions.Item>
                  <Descriptions.Item label="Compteur profil">
                    {fmt(profile.stats?.missionsCompletees)} terminées · {fmt(profile.stats?.totalGains)} F de gains
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card size="small" title={<Space><PhoneOutlined />Contact</Space>}>
                <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', width: 90 }}>
                  <Descriptions.Item label="Téléphone">{formatPhoneCI(livreur.phone)}</Descriptions.Item>
                  <Descriptions.Item label="Email">{livreur.email || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Ville">{profile.cityId?.name || '—'}</Descriptions.Item>
                </Descriptions>
                <Button block icon={<PhoneOutlined />} href={`tel:${livreur.phone}`}>
                  Appeler le livreur
                </Button>
              </Card>

              <Card size="small" title={<Space><CarOutlined />Moto & permis</Space>}>
                <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', width: 130 }}>
                  <Descriptions.Item label="Plaque">
                    {profile.plaqueImmatriculation || <Text type="secondary">Non renseignée</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="N° de permis">
                    {profile.numeroPermisConduire || <Text type="secondary">Non renseigné</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Couleur">
                    {profile.couleurMoto || <Text type="secondary">Non renseignée</Text>}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card size="small" title={<Space><WalletOutlined />Compte</Space>}>
                <div style={{ marginBottom: 10 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Solde de points</Text>
                  <div style={{ fontSize: 22, fontWeight: 700, color: (livreur.soldePoints?.point ?? 0) < 0 ? '#f5222d' : '#52c41a' }}>
                    {fmt(livreur.soldePoints?.point ?? 0)}
                  </div>
                </div>
                <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', width: 120 }}>
                  <Descriptions.Item label="Statut">
                    <Tag color={statusColor(profile.status)}>{statusLabel(profile.status)}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Disponibilité">
                    <Tag color={profile.isAvailable ? 'green' : 'default'}>
                      {profile.isAvailable ? 'En ligne' : 'Hors ligne'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Inscrit le">
                    {profile.registeredAt ? dayjs(profile.registeredAt).format('DD/MM/YYYY') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Approuvé le">
                    {profile.approvedAt ? dayjs(profile.approvedAt).format('DD/MM/YYYY') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rôle principal">{livreur.role || '—'}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default LivreurDetail;
